import { useSyncExternalStore } from "react";

/**
 * Tiny module-level store of projects with AI runs in flight, written by the
 * GlobalAiRunWatcher pollers and read by any surface that wants to show run
 * state (indicator pill, project list chips) without prop drilling.
 */
export interface ActiveAiRunEntry {
  projectId: string;
  projectName?: string;
  workspaceId?: string;
  features: string[];
}

let snapshot: Record<string, ActiveAiRunEntry> = {};
const listeners = new Set<() => void>();

const emit = () => {
  for (const listener of listeners) listener();
};

export const setActiveAiRun = (projectId: string, entry: ActiveAiRunEntry | null) => {
  const existing = snapshot[projectId];
  if (entry === null) {
    if (!existing) return;
    const next = { ...snapshot };
    delete next[projectId];
    snapshot = next;
    emit();
    return;
  }
  if (
    existing &&
    existing.projectName === entry.projectName &&
    existing.workspaceId === entry.workspaceId &&
    existing.features.length === entry.features.length &&
    existing.features.every((f, i) => f === entry.features[i])
  ) {
    return;
  }
  snapshot = { ...snapshot, [projectId]: entry };
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => snapshot;

export const useActiveAiRuns = (): Record<string, ActiveAiRunEntry> =>
  useSyncExternalStore(subscribe, getSnapshot);
